const BIRD_Y_VARS = [
  "--tpl-bird-y0",
  "--tpl-bird-y1",
  "--tpl-bird-y2",
  "--tpl-bird-y3",
] as const;

// keyframes の X 移動量（-48vw → 128vw）
const BIRD_PATH_X_SPAN_VW = 176;
const BIRD_PATH_ANGLE_MIN = 0;
const BIRD_PATH_ANGLE_MAX = 75;

// セクション高さあたりの片側羽数（商品リスト約1500px/3羽を基準）
const HEIGHT_PER_SIDE_BIRD = 500;
const MIN_BIRDS_PER_SIDE = 1;
const MAX_BIRDS_PER_SIDE = 3;

const BIRD_SIZE = {
  "1": { width: 291, height: 288 },
  "2": { width: 359, height: 310 },
} as const;

const LEFT_TOPS_BY_COUNT: Record<number, number[]> = {
  1: [28],
  2: [12, 46],
  3: [6, 24, 36],
};

const RIGHT_TOPS_BY_COUNT: Record<number, number[]> = {
  1: [42],
  2: [22, 58],
  3: [14, 37, 52],
};

type BirdSide = "left" | "right";
type BirdAssetKey = keyof typeof BIRD_SIZE;

function getPathSpanPx(): number {
  return (window.innerWidth * BIRD_PATH_X_SPAN_VW) / 100;
}

/** 角度の大きさ（deg・0〜75）から Y 方向の移動量を算出 */
function getPathDeltaY(angleDeg: number, pathSpanPx: number): number {
  const clampedAngle = Math.max(
    BIRD_PATH_ANGLE_MIN,
    Math.min(BIRD_PATH_ANGLE_MAX, angleDeg),
  );
  return Math.round(
    Math.tan((clampedAngle * Math.PI) / 180) * pathSpanPx,
  );
}

/** 斜めの飛行導線（Y 座標）を生成 */
function generateBirdPath(deltaY: number): number[] {
  const y0 = Math.round(-deltaY / 2);
  const y3 = Math.round(deltaY / 2);
  const y1 = Math.round(y0 + (y3 - y0) * 0.38);
  const y2 = Math.round(y0 + (y3 - y0) * 0.74);

  return [y0, y1, y2, y3];
}

function getAdaptiveSection(root: HTMLElement): HTMLElement | null {
  return root.closest<HTMLElement>("section");
}

/** 鳥 CSS 飛行（is-bird-ready）を止める */
function clearBirdReadyForRoot(root: HTMLElement): void {
  if (root.dataset.birdDynamic === "true") {
    getAdaptiveSection(root)?.classList.remove("is-bird-ready");
    return;
  }

  root
    .closest<HTMLElement>('[id^="TopProductList-"], [id^="CollectionGrid-"]')
    ?.classList.remove("is-bird-ready");
}

/** トップ商品リストは従来どおり ID セレクタで ready を付与 */
function markBirdReady(root: HTMLElement): void {
  if (root.dataset.birdDynamic === "true") {
    getAdaptiveSection(root)?.classList.add("is-bird-ready");
    return;
  }

  root
    .closest<HTMLElement>('[id^="TopProductList-"], [id^="CollectionGrid-"]')
    ?.classList.add("is-bird-ready");
}

/** 鳥 img の読み込み完了を待つ（lazy 相当の遅延を避けてから CSS 飛行を開始） */
function waitForBirdImages(root: HTMLElement): Promise<void> {
  const imgs = root.querySelectorAll<HTMLImageElement>(
    ".p-top-product-list__bird-el",
  );
  if (imgs.length === 0) return Promise.resolve();

  return Promise.all(
    Array.from(imgs).map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.addEventListener("load", () => resolve(), { once: true });
          img.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

function applyBirdPathVars(root: HTMLElement): void {
  const angleDeg = Math.abs(Number(root.dataset.birdPathAngle) || 20);
  // 左→右上 / 右→左上の上向き導線（角度は 0〜75° の大きさのみ）
  const deltaY = -getPathDeltaY(angleDeg, getPathSpanPx());

  root
    .querySelectorAll<HTMLElement>("[data-js-top-product-list-bird]")
    .forEach((bird) => {
      generateBirdPath(deltaY).forEach((value, index) => {
        bird.style.setProperty(BIRD_Y_VARS[index], `${value}px`);
      });
    });
}

function applyBirdPaths(root: HTMLElement, domRebuilt = false): void {
  if (domRebuilt) clearBirdReadyForRoot(root);
  applyBirdPathVars(root);

  void waitForBirdImages(root).then(() => {
    markBirdReady(root);
  });
}

function getAdaptiveBirdLimits(root: HTMLElement): {
  maxPerSide: number;
  heightStep: number;
} {
  const maxPerSide = Number(root.dataset.birdMaxPerSide);
  const heightStep = Number(root.dataset.birdHeightStep);

  return {
    maxPerSide:
      Number.isFinite(maxPerSide) && maxPerSide > 0
        ? maxPerSide
        : MAX_BIRDS_PER_SIDE,
    heightStep:
      Number.isFinite(heightStep) && heightStep > 0
        ? heightStep
        : HEIGHT_PER_SIDE_BIRD,
  };
}

function getBirdsPerSide(root: HTMLElement, zoneHeight: number): number {
  const { maxPerSide, heightStep } = getAdaptiveBirdLimits(root);

  return Math.max(
    MIN_BIRDS_PER_SIDE,
    Math.min(
      maxPerSide,
      Math.round(zoneHeight / heightStep) || MIN_BIRDS_PER_SIDE,
    ),
  );
}

/** 羽数に応じた縦位置（3羽以下は従来値、4羽以上は均等配置） */
function getBirdTopPercents(count: number, side: BirdSide): number[] {
  const preset =
    side === "left" ? LEFT_TOPS_BY_COUNT[count] : RIGHT_TOPS_BY_COUNT[count];
  if (preset) return preset;

  const minTop = 8;
  const maxTop = 72;
  if (count <= 1) {
    return [side === "left" ? 28 : 42];
  }

  return Array.from({ length: count }, (_, index) =>
    Math.round(minTop + ((maxTop - minTop) * index) / (count - 1)),
  );
}

function getAssetKey(side: BirdSide, index: number): BirdAssetKey {
  const even = index % 2 === 0;
  if (side === "left") return even ? "1" : "2";
  return even ? "2" : "1";
}

function createBird(options: {
  side: BirdSide;
  variant: number;
  topPercent: number;
  src: string;
  assetKey: BirdAssetKey;
}): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = `p-top-product-list__bird p-top-product-list__bird--${options.side}-${options.variant}`;
  wrap.setAttribute("data-js-top-product-list-bird", "");
  wrap.style.top = `${options.topPercent}%`;

  const img = document.createElement("img");
  img.className = "p-top-product-list__bird-el";
  if (options.side === "right") {
    img.classList.add("p-top-product-list__bird-el--flip");
  }
  img.src = options.src;
  img.width = BIRD_SIZE[options.assetKey].width;
  img.height = BIRD_SIZE[options.assetKey].height;
  img.alt = "";
  // コレクション等: 遅延読み込み・低優先度 decode を避ける
  img.loading = "eager";
  img.decoding = "sync";
  img.fetchPriority = "high";
  wrap.append(img);

  return wrap;
}

function appendSideBirds(
  root: HTMLElement,
  side: BirdSide,
  count: number,
  srcByKey: Record<BirdAssetKey, string>,
): void {
  const tops = getBirdTopPercents(count, side);

  tops.forEach((topPercent, index) => {
    const assetKey = getAssetKey(side, index);
    const src = srcByKey[assetKey];
    if (!src) return;

    root.append(
      createBird({
        side,
        variant: (index % MAX_BIRDS_PER_SIDE) + 1,
        topPercent,
        src,
        assetKey,
      }),
    );
  });
}

/** セクション高さに合わせて鳥の DOM を組み直す（差し替えたら true） */
function syncAdaptiveBirds(root: HTMLElement): boolean {
  if (root.dataset.birdDynamic !== "true") return false;

  const srcByKey: Record<BirdAssetKey, string> = {
    "1": root.dataset.birdSrc1 ?? "",
    "2": root.dataset.birdSrc2 ?? "",
  };
  if (!srcByKey["1"] || !srcByKey["2"]) return false;

  // 空の絶対配置コンテナは高さが 0 になるため、親セクションの高さを使う
  const sectionHeight = getAdaptiveSection(root)?.clientHeight ?? 0;
  const perSide = getBirdsPerSide(root, sectionHeight);
  const total = perSide * 2;
  const current = root.querySelectorAll(
    "[data-js-top-product-list-bird]",
  ).length;
  if (current === total) return false;

  root.replaceChildren();
  appendSideBirds(root, "left", perSide, srcByKey);
  appendSideBirds(root, "right", perSide, srcByKey);
  return true;
}

const observedRoots = new WeakSet<HTMLElement>();

function observeAdaptiveRoot(root: HTMLElement): void {
  if (root.dataset.birdDynamic !== "true") return;
  if (observedRoots.has(root)) return;
  if (typeof ResizeObserver === "undefined") return;

  const section = getAdaptiveSection(root);
  if (!section) return;

  observedRoots.add(root);
  // 画像読み込みでセクション高さが変わったときに羽数を追従させる
  const observer = new ResizeObserver(() => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const domRebuilt = syncAdaptiveBirds(root);
      applyBirdPaths(root, domRebuilt);
    }, 150);
  });
  observer.observe(section);
}

function initTopProductListBirds(root: HTMLElement): void {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // トップ商品リストのみ Liquid 固定6羽。それ以外の adaptive は JS で羽数調整
  if (root.dataset.birdDynamic === "true") {
    const domRebuilt = syncAdaptiveBirds(root);
    applyBirdPaths(root, domRebuilt);
    observeAdaptiveRoot(root);
    return;
  }

  applyBirdPaths(root);
}

let resizeTimer = 0;
let didBindResize = false;

export function initTopProductListBirdsAll(): void {
  document
    .querySelectorAll<HTMLElement>("[data-js-top-product-list-birds]")
    .forEach(initTopProductListBirds);

  if (didBindResize) return;
  didBindResize = true;

  window.addEventListener("resize", () => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document
        .querySelectorAll<HTMLElement>("[data-js-top-product-list-birds]")
        .forEach((root) => {
          if (root.dataset.birdDynamic === "true") {
            const domRebuilt = syncAdaptiveBirds(root);
            applyBirdPaths(root, domRebuilt);
            return;
          }
          applyBirdPathVars(root);
        });
    }, 150);
  });
}
